import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  adjustComposition,
  composedTotal,
  compositionToCalculation,
  computePlates,
  convertWeight,
  defaultLoadableStepFor,
  loadablePlateCount,
  nearestLoadable,
  nextUnit,
  plateLoadModeFor,
  smallestLoadableStep,
  type Exercise,
  type PlateCalculation,
  type PlateLoadMode,
  type PlatePairLoaded,
  parseLocaleNumber,
} from '@heraion/shared';

import {
  BAR_OPTIONS,
  loadPlateLoadShape,
  persistPlateLoadShape,
  type PlateLoadShape,
} from './state/plateConfig';
import { convertWeightText } from '../convertWeightText';
import { formatUnitValue, weightLocale } from './plateCalcMath';
import type { WeightUnit } from './state/loggerDraft';
import { useDisclosure } from '../primitives/useDisclosure';
import { usePlateBarInventory } from './usePlateBarInventory';

// Everything `PlateCalcModal` KNOWS, extracted so the modal is only what it
// SHOWS. Lifted out whole in fetta C — the calculator's keypad needed the room
// under the 500-line cap, and the split falls where the other three already
// did (`PlateCalcInputsSection`, `PlateCalcSummary`, `PlateCalcComposeSection`
// are the view halves of this same screen).
//
// ⭐ LG-37b fetta E1 — everything in here is in the ROW's unit (`unit`), not in
// kilos. The bar, the inventory, the working value, the plates, the remainder:
// all of them are what is written on that gym's iron. Canonical kilos survive
// at exactly two borders — `initialKg` coming in and `targetKg` going out —
// because the draft persists kg and `usePlateCalc.apply` has not changed its
// contract. Everything between the two speaks the athlete's unit.
//
// ⛔ The unit comes from the SET, never from the profile preference. `toggle_unit`
// is a per-row action (loggerActions.ts:228), so a modal reading the profile
// would be deaf to the very gesture that opens this bug (inbox 66c51036), and
// a modal showing kg while apply writes lb is `LG-81` again — the right number
// under the wrong unit.

type Params = {
  visible: boolean;
  initialKg: number;
  unit: WeightUnit;
  exercise: Exercise | null;
  isBodyweight: boolean;
  initialNegative: boolean;
};

export function usePlateCalcModalState({
  visible,
  initialKg,
  unit: seedUnit,
  exercise,
  isBodyweight,
  initialNegative,
}: Params) {
  // ⭐ LG-93 — the unit became a VIEW choice, and the rename is the whole
  // mechanism: everything below still reads `unit`, it just no longer comes
  // straight from the prop. The set keeps its own unit and `apply` still
  // formats against THAT (`usePlateCalc.ts`), so looking at the bar in pounds
  // never rewrites a metric row.
  //
  // ⛔ Seeded from the set on every open, never sticky: reopening on a metric
  // row must not inherit the pounds someone looked at three exercises ago —
  // that is LG-92's lesson, and this modal does not unmount.
  const [unit, setUnit] = useState<WeightUnit>(seedUnit);
  useEffect(() => {
    if (visible) setUnit(seedUnit);
  }, [visible, seedUnit]);
  // The athlete's decimal mark, for every number this hook writes into the
  // weight field. ⚠️ Through `weightLocale` and not `i18n.resolvedLanguage`:
  // the suites that mount this modal mock `react-i18next` with a `t` and no
  // `i18n` at all, and the bare read throws before the modal renders a row.
  const { i18n } = useTranslation();
  const locale = weightLocale(i18n);
  const [target, setTarget] = useState('');
  const [loadShape, setLoadShape] = useState<PlateLoadShape>('standard');
  // Plates the athlete put on by hand, or `null` for "the greedy is driving".
  // Not a mirror of the target: the two directions take turns, and which one
  // is live is exactly what this null means.
  //
  // ⚠️ It carries the SHAPE and the UNIT it was built under, and both are
  // load-bearing. The counts mean pairs under 'pairs' and single plates under
  // 'single', so a composition read back under the other one silently doubles
  // or halves the bar; the sizes are face values, so a metric composition read
  // against a pound bar adds 20 lb where the athlete hung 20 kg. Stamping them
  // means the mismatch is answered where the value is USED — below, in two
  // lines — instead of by a reset at every door that can change either. There
  // are four such doors (the shape chip, the catalog seed, the stored-preference
  // read that lands a tick later, and now the row's unit) and a guard spread
  // across them dies in the copy nobody updated
  // [[feedback_a_gate_wired_in_two_places_dies_in_the_quiet_one]].
  const [composition, setComposition] = useState<{
    mode: PlateLoadMode;
    unit: WeightUnit;
    plates: PlatePairLoaded[];
  } | null>(null);
  // Bodyweight sign: positive = weighted (extra plate on belt), negative
  // = assist (band / machine). Synced from set.extraNegative on open via
  // the visible-prop effect below.
  const [negative, setNegative] = useState(false);
  // Cambia a ogni riseeding del valore di lavoro: è la `key` dei figli che
  // tengono uno stato loro (il nastro della calcolatrice, il suo chip
  // dell'unità, il campo della barra personalizzata). ⛔ Non un elenco di
  // reset a mano: gli stati sono tre in due componenti, e un elenco scritto a
  // mano è quello che dimentica il quarto.
  const [openKey, setOpenKey] = useState(0);

  const slug = exercise?.slug ?? '';
  // A catalog bar exists only when positive; 0 means absent (all rows today).
  const catalogBarKg =
    typeof exercise?.defaultBarKg === 'number' && exercise.defaultBarKg > 0
      ? exercise.defaultBarKg
      : null;
  const { bar, inventory, seedBar, releaseBar, pickBar, setPairs } =
    usePlateBarInventory(unit, catalogBarKg);
  // The calculator is an expansion, so its open/closed state is a disclosure:
  // the same hook every other foldable box in the app uses. Owned HERE so the
  // reset on reopen (LG-87) lives with the other open-time resets, in one
  // mechanism.
  const calculator = useDisclosure();
  const { setExpanded: setCalculatorExpanded } = calculator;
  // ⛔ Folds on OPEN, never on close: folding on close would collapse the
  // keypad during the slide-out. And only on `visible`, not in the reseed
  // effect below, which also re-runs when the unit rotates mid-open.
  useEffect(() => {
    if (visible) setCalculatorExpanded(false);
  }, [visible, setCalculatorExpanded]);
  const catalogShape: PlateLoadShape =
    exercise?.barLoading === 'single-side' ? 'single-side' : 'standard';
  // The calculator's word for the same two shapes.
  // CN-39 — `plateLoadModeFor`, non la traduzione a mano: era la terza copia
  // della stessa riga, e la quarta era quella che non c'era (`roundingStepFor`
  // cablava `'pairs'`), cioè il baco.
  const mode: PlateLoadMode = plateLoadModeFor(loadShape);

  // ⭐ LG-86 — TWO uses of the composition, two guards, both HERE and neither
  // at a door.
  //
  // `owned`: the composition still speaks the athlete's unit, so the number in
  // the FIELD came out of it and must keep coming out of it. Changing the SHAPE
  // does not break that link — the plates hanging on the bar are the same
  // plates — which is why this guard does not look at `mode`.
  //
  // `composed`: it can also be RENDERED, and that needs the shape to agree too,
  // because the counts mean different iron under the other one.
  //
  // ⛔ The old comment here claimed a stale composition "isn't converted — it's
  // dropped". It was false: masking is not dropping, and the masked one came
  // back alive. It came back out of step, too — `onSelectBar` re-wrote the
  // field only while the composition was VISIBLE, so a bar changed under the
  // other shape moved the summary and left the field on the old total (compose
  // 60 on a 20 bar, switch to one side, drop to a 15 bar, switch back: field
  // 60, summary 55). It is not dropped, it is hidden and it returns — and the
  // field follows it the whole time it is away.
  const owned = composition && composition.unit === unit ? composition : null;
  const composed = owned && owned.mode === mode ? owned.plates : null;

  // Reseed the input on every open so consecutive opens on different
  // rows pick up the latest weight value. Sign tracks the row's existing
  // extraNegative on bodyweight rows.
  //
  // ⭐ LG-91: the bar is re-seeded here too (`usePlateBarInventory` owns the
  // precedence).
  //
  // ⚠️ `initialKg` is CANONICAL and the field is not, so it converts on the way
  // in. `convertWeight` and not `fromCanonicalKg`: the rounding belongs to the
  // formatter one line later, and doing it twice is how a 2.5 lb plate becomes
  // a 2.4 one.
  useEffect(() => {
    if (!visible) {
      releaseBar();
      return;
    }
    const seed = convertWeight(initialKg, 'kg', unit);
    setTarget(seed > 0 ? formatUnitValue(seed, unit, locale) : '');
    setComposition(null);
    // ⛔ LG-92 — e con il valore riparte anche l'IDENTITÀ dei figli.
    //
    // `LoggerModals` monta `PlateCalcModal` SEMPRE e muove solo `visible`, così
    // l'animazione di uscita può girare ⇒ ogni `useState` di un figlio cammina
    // da un esercizio all'altro. Antenore (11/09): riaperta la calcolatrice su
    // un altro esercizio, ci ha trovato il peso di quello prima — e con lui
    // l'unità di prima, che è la forma in cui un numero cresce a ogni giro.
    //
    // ⭐ Il contatore vive QUI, dentro l'effetto che risemina il campo, e non
    // in un effetto suo nel modal: là cambiava un commit TROPPO PRESTO — i
    // figli rinascevano, leggevano il valore VECCHIO e lo congelavano nel loro
    // seme prima che questa riga scrivesse quello nuovo. *L'identità dei figli
    // e il valore da cui ripartono devono cambiare nello stesso commit.*
    setOpenKey((n) => n + 1);
    seedBar();
    setNegative(isBodyweight && initialNegative);
  }, [
    visible,
    initialKg,
    unit,
    locale,
    releaseBar,
    seedBar,
    isBodyweight,
    initialNegative,
  ]);

  // The shape the athlete last chose FOR THIS EXERCISE, else the catalog seed.
  // Seeded synchronously from the catalog first so the summary never renders a
  // shape the row contradicts, then corrected when the stored read lands —
  // `null` from storage means "never chose", which is why it can't be collapsed
  // into a default: it's what lets the catalog win.
  useEffect(() => {
    if (!visible) return;
    setLoadShape(catalogShape);
    let cancelled = false;
    void (async () => {
      const stored = await loadPlateLoadShape(slug);
      if (!cancelled && stored) setLoadShape(stored);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, slug, catalogShape]);

  const onSelectBar = (value: number) => {
    pickBar(value);
    // Composing means "bar + what I hung on it", so a lighter bar makes the
    // same plates weigh less. Under the greedy the target is what the athlete
    // asked for and a new bar only re-cuts it, which is why this re-writes the
    // value in one direction and not the other.
    //
    // ⛔ `owned`, not `composed`, and in the composition's OWN mode — that pair
    // of words is the LG-86 fix. A composition hidden behind the other shape
    // still owns this field; forgetting it here is what let the field and the
    // summary disagree the moment the shape came back.
    if (owned) {
      setTarget(
        formatUnitValue(
          composedTotal(owned.plates, value, owned.mode),
          unit,
          locale,
        ),
      );
    }
  };

  const onSelectShape = (shape: PlateLoadShape) => {
    setLoadShape(shape);
    void persistPlateLoadShape(slug, shape);
    // ⛔ No reset here on purpose: `composed` already stops answering the
    // moment the mode moves, whichever door moved it.
  };

  // Typing is the other direction. It doesn't merge with a composition, it
  // replaces it — the field is the athlete saying "no, the number is this".
  // The calculator's "copy into the weight" is the same gesture by another
  // hand, so it comes through here too.
  const onEditTarget = (text: string) => {
    setComposition(null);
    setTarget(text);
  };

  // Editing what the gym HAS can invalidate what is already on the bar
  // (four pairs composed, inventory dropped to two). Rather than clamp a
  // composition the athlete can no longer see being clamped, hand the value
  // back to the greedy, which re-solves the same total against the new
  // inventory and says so if it can't.
  const onSetPairs = (size: number, pairs: number) => {
    setPairs(size, pairs);
    setComposition(null);
  };

  // The working value as a NUMBER, parsed once, IN THE ROW'S UNIT.
  // `parseLocaleNumber` so "20,5" on de-DE / fr-FR / it-IT decimal-pad
  // keyboards reads as 20.5 instead of truncating to 20. NaN when the field is
  // empty or nonsense.
  //
  // ⚠️ Returned, not kept private, because three callers now need the same
  // number: the solver below, the Apply CTA, and the calculator's seed. Three
  // `parseLocaleNumber(target)` in three files is how they drift the day the
  // parsing rule gains a case.
  const targetValue = parseLocaleNumber(target);
  // …and the same value at the border, in canonical kilos. The ONLY two things
  // that want this are Apply (the draft persists kg) and the calculator's seed
  // (`CalcState.placedKg` is canonical, `LG-85`). Nothing that renders does.
  const targetKg = convertWeight(targetValue, unit, 'kg');

  const calc: PlateCalculation = useMemo(() => {
    // A live composition IS the answer — it was built plate by plate, so
    // re-deriving it from its own total would let the greedy overrule the
    // athlete's hands.
    if (composed) {
      return compositionToCalculation(composed, bar, mode);
    }
    if (!Number.isFinite(targetValue)) {
      return { ok: false, reason: 'invalid_input' };
    }
    return computePlates({
      target: targetValue,
      bar,
      inventory,
      barLoading: loadShape,
    });
  }, [composed, targetValue, bar, inventory, loadShape, mode]);

  // One tap = one plate on or off. The starting point when nothing has been
  // composed yet is the load the summary is ALREADY showing, so the first tap
  // continues the bar in front of you instead of clearing it.
  const onComposeDelta = (size: number, delta: number) => {
    const base = composed ?? (calc.ok ? calc.pairsLoaded : []);
    const next = adjustComposition(
      base,
      size,
      delta,
      loadablePlateCount(inventory, size, mode),
    );
    setComposition({ mode, unit, plates: next });
    setTarget(formatUnitValue(composedTotal(next, bar, mode), unit, locale));
  };

  // ⭐ LG-93 — «≈»: porta il numero al carico più vicino che l'atleta può
  // DAVVERO mettere sul ferro. Sue due decisioni dell'11/09 notte, e nessuna
  // delle due è neutra:
  //
  // ⭐ **Al più vicino**, non per difetto. `roundLoadable` floora, ed è giusto
  // per una prescrizione — non chiedere mai più del previsto — ma qui il gesto
  // è dell'atleta su un numero che sta guardando, e perdere 2,5 kg che non ha
  // chiesto di perdere sarebbe una risposta sbagliata alla sua domanda.
  //
  // ⭐⭐ **Al netto della barra**, ed è la metà che si sbaglia in silenzio. Con
  // un bilanciere da 20 e passo 2,5 arrotondare il TOTALE funziona per caso,
  // perché 20 è già multiplo di 2,5. Con un curl bar da 7 ogni multiplo di 2,5
  // del totale è un peso che NON si può caricare: il bottone proporrebbe
  // esattamente ciò che promette di evitare. ⇒ si toglie la barra, si arrotonda
  // ciò che ci va sopra, si rimette la barra.
  //
  // ⛔ E il passo esce dall'inventario dell'atleta PER FORMA DI CARICO: un
  // disco da 1,25 muove 2,5 kg a coppie e 1,25 su una landmine. `plateStep`
  // dentro `smallestLoadableStep` fa il ×2/×1.
  // ⚠️ Qui c'era scritto che `roundingStepFor` cabla `'pairs'` «ed è il motivo
  // per cui non lo si chiama qui». Dal 14/09 non è più vero: `CN-39` gli ha
  // dato il mode, e il contesto porta ora entrambi i passi. Questo call site
  // resta comunque a sé perché sottrae la BARRA prima di snappare, che è
  // lavoro del chiamante e non del contesto.
  // ⭐ È anche il primo punto dell'app che passa davvero uno step in LIBBRE:
  // `stepLb` esisteva come porta e nessuno ci era mai entrato.
  const snapToLoadable = () => {
    if (!Number.isFinite(targetValue) || targetValue <= 0) return;
    const overBar = targetValue - bar;
    // Sotto la barra non c'è niente da arrotondare: il ferro è la barra nuda,
    // e il numero è già quello che è.
    if (overBar <= 0) return;
    const step = smallestLoadableStep(
      inventory,
      mode,
      defaultLoadableStepFor(unit),
    );
    const snappedKg = nearestLoadable(
      convertWeight(overBar, unit, 'kg'),
      unit,
      unit === 'kg' ? { stepKg: step } : { stepLb: step },
    );
    const snapped = convertWeight(snappedKg, 'kg', unit) + bar;
    setTarget(formatUnitValue(snapped, unit, locale));
    // La composizione non sopravvive a un salto: i dischi che c'erano non
    // fanno più quel numero.
    setComposition(null);
  };

  // ⭐ LG-93 — un tap ruota l'unità, e il numero che hai davanti VIENE CON TE.
  //
  // ⛔ Non un re-seed da `initialKg`: l'effetto di apertura fa quello, ed è
  // giusto lì, ma qui butterebbe via ciò che l'atleta ha digitato o composto.
  // `convertWeightText` è la primitiva dell'ondata 0 di `CN-26` e porta il
  // canonico come seme, così kg → lb → kg torna al numero di partenza invece
  // di atterrare sui grammi (`LG-85`).
  //
  // ⚠️ La composizione NON si converte e non deve: `owned` la scarta da sola
  // quando l'unità non combacia più, perché i dischi di una palestra metrica
  // non sono quelli di una imperiale — sono ferro diverso, non lo stesso ferro
  // scritto in un altro modo.
  const cycleUnit = () => {
    const next = nextUnit(unit);
    setTarget((prev) => convertWeightText(prev, unit, next, initialKg));
    setUnit(next);
  };

  return {
    unit,
    cycleUnit,
    snapToLoadable,
    target,
    // ⛔ `targetValue` stays inside. Its two outside callers both want the
    // BORDER value: Apply persists kg and the calculator's `placedKg` is kg.
    // Handing out the working-unit number as well would be a second weight on
    // the same screen for a caller to pick wrong.
    targetKg,
    bar,
    barOptions: BAR_OPTIONS[unit],
    inventory,
    loadShape,
    negative,
    setNegative,
    mode,
    // ⛔ `composed` stays inside. The view never asks whether a composition is
    // live — it reads `calc`, which already IS the composition when there is
    // one. Handing it out would invite a second answer to the same question.
    calc,
    onSelectBar,
    onSelectShape,
    onEditTarget,
    setPairs: onSetPairs,
    onComposeDelta,
    calculator,
    openKey,
  };
}
